import React, {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  PublicGisLookupData,
  PublicGisLookupErrorType,
  PublicGisLookupIdentifier,
  PublicGisMatchedOrder,
} from "./types";
import { readGuestLookupPrefill } from "@app/pages/private/scheduleAGeneticInformationSession/context";

/**
 * Funnel-scoped state for the anonymous order-lookup GIS flow. Kept separate
 * from the draw-request `PublicMpStepperData` provider so the GIS funnel is
 * fully decoupled. A submitted lookup is restored from the guest session cache
 * when the visitor returns from intake; starting a new guest flow clears it.
 *
 * Note: the patient's selected language lives in `IntlContext`, not here, and
 * is never sent to the server.
 */
export interface PublicGisOrderLookupController {
  lookupData?: PublicGisLookupData;
  saveLookupData: (data: PublicGisLookupData) => void;
  lookupIdentifier?: PublicGisLookupIdentifier;
  setLookupIdentifier: (identifier: PublicGisLookupIdentifier) => void;

  recaptchaValue: string | null;
  setRecaptchaValue: (value: string | null) => void;

  guestToken?: string;
  setGuestToken: (token: string) => void;

  matchedOrder?: PublicGisMatchedOrder;
  setMatchedOrder: (order: PublicGisMatchedOrder) => void;

  // Eligible orders for the multi-order picker (one per test type).
  matchedOrders: PublicGisMatchedOrder[];
  setMatchedOrders: (orders: PublicGisMatchedOrder[]) => void;

  errorType?: PublicGisLookupErrorType;
  setErrorType: (errorType: PublicGisLookupErrorType) => void;
}

const defaultController: PublicGisOrderLookupController = {
  lookupData: undefined,
  saveLookupData: () => undefined,
  lookupIdentifier: undefined,
  setLookupIdentifier: () => undefined,
  recaptchaValue: null,
  setRecaptchaValue: () => undefined,
  guestToken: undefined,
  setGuestToken: () => undefined,
  matchedOrder: undefined,
  setMatchedOrder: () => undefined,
  matchedOrders: [],
  setMatchedOrders: () => undefined,
  errorType: undefined,
  setErrorType: () => undefined,
};

export const PublicGisOrderLookupContext =
  createContext<PublicGisOrderLookupController>(defaultController);

PublicGisOrderLookupContext.displayName = "PublicGisOrderLookupContext";

interface ProviderProps {
  children: ReactNode;
}

const readInitialLookupData = (): PublicGisLookupData | undefined => {
  const prefill = readGuestLookupPrefill();
  if (!prefill) return undefined;

  return {
    firstName: prefill.firstName ?? "",
    lastName: prefill.lastName ?? "",
    dateOfBirth: prefill.dateOfBirth ?? "",
    mobileNumber: prefill.phone ?? "",
    email: prefill.email ?? "",
    zipCode: prefill.zipCode ?? "",
  };
};

const readInitialLookupIdentifier = (): PublicGisLookupIdentifier | undefined =>
  readGuestLookupPrefill()?.lookupIdentifier;

const PublicGisOrderLookupProvider: React.FC<ProviderProps> = ({
  children,
}) => {
  const [lookupData, setLookupData] = useState<PublicGisLookupData | undefined>(
    readInitialLookupData
  );
  const [lookupIdentifier, setLookupIdentifier] = useState<
    PublicGisLookupIdentifier | undefined
  >(readInitialLookupIdentifier);
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const [guestToken, setGuestToken] = useState<string>();
  const [matchedOrder, setMatchedOrder] = useState<PublicGisMatchedOrder>();
  const [matchedOrders, setMatchedOrders] = useState<PublicGisMatchedOrder[]>(
    []
  );
  const [errorType, setErrorType] = useState<PublicGisLookupErrorType>();

  const saveLookupData = useCallback(
    (data: PublicGisLookupData) => setLookupData(data),
    []
  );

  const value = useMemo<PublicGisOrderLookupController>(
    () => ({
      lookupData,
      saveLookupData,
      lookupIdentifier,
      setLookupIdentifier,
      recaptchaValue,
      setRecaptchaValue,
      guestToken,
      setGuestToken,
      matchedOrder,
      setMatchedOrder,
      matchedOrders,
      setMatchedOrders,
      errorType,
      setErrorType,
    }),
    [
      lookupData,
      lookupIdentifier,
      recaptchaValue,
      guestToken,
      matchedOrder,
      matchedOrders,
      errorType,
    ]
  );

  return (
    <PublicGisOrderLookupContext.Provider value={value}>
      {children}
    </PublicGisOrderLookupContext.Provider>
  );
};

export default PublicGisOrderLookupProvider;
